import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Palette, Users, ShoppingBag, Truck, Send, Mail } from "lucide-react";

export default function AdminBroadcast() {
  const navigate = useNavigate();
  const [audienceCounts, setAudienceCounts] = useState({ founding: 0, newsletter: 0, customers: 0, all: 0 });
  const [form, setForm] = useState({ audience: "founding", subject: "", body: "" });
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    api.get("/admin/broadcast/preview").then((r) => setAudienceCounts(r.data)).catch(() => {});
  }, []);

  const send = async () => {
    if (!form.subject.trim() || !form.body.trim()) {
      toast.error("Subject and message are both required");
      return;
    }
    const size = audienceCounts[form.audience] ?? 0;
    if (size === 0) {
      toast.error("This audience has zero recipients");
      return;
    }
    if (!window.confirm(`Send to ${size} recipient${size === 1 ? "" : "s"}? This can't be undone.`)) return;
    setSending(true);
    try {
      const bodyHtml = form.body.split("\n\n").map((p) => `<p style="margin:0 0 16px 0;font-size:16px;line-height:1.6;color:#3C3E3C;">${p.replace(/\n/g, "<br/>")}</p>`).join("");
      const { data } = await api.post("/admin/broadcast", {
        subject: form.subject,
        html: bodyHtml,
        audience: form.audience,
      });
      setLastResult(data);
      toast.success(`Sent to ${data.sent} of ${data.audience_size} recipients`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("jenova_admin_token");
    navigate("/admin/login");
  };

  return (
    <div className="pt-24 md:pt-28 pb-24 min-h-screen bg-white">
      <div className="max-w-[900px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between border-b border-[#E5E2DC] pb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-stone-500 font-semibold">Studio</p>
            <h1 className="mt-2 font-serif text-4xl md:text-5xl font-light tracking-tight">
              Broadcast <em className="italic text-stone-600">email</em>.
            </h1>
            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm">
              <Link to="/admin" className="text-stone-500 hover:text-[#1A1C1A] pb-1 inline-flex items-center gap-1.5"><ArrowLeft strokeWidth={1.5} className="w-4 h-4" /> Products</Link>
              <Link to="/admin/site" className="text-stone-500 hover:text-[#1A1C1A] pb-1 inline-flex items-center gap-1.5"><Palette strokeWidth={1.5} className="w-4 h-4" /> Site Content</Link>
              <Link to="/admin/founding" className="text-stone-500 hover:text-[#1A1C1A] pb-1 inline-flex items-center gap-1.5"><Users strokeWidth={1.5} className="w-4 h-4" /> Founding Members</Link>
              <Link to="/admin/orders" className="text-stone-500 hover:text-[#1A1C1A] pb-1 inline-flex items-center gap-1.5"><ShoppingBag strokeWidth={1.5} className="w-4 h-4" /> Orders</Link>
              <Link to="/admin/shipping-settings" className="text-stone-500 hover:text-[#1A1C1A] pb-1 inline-flex items-center gap-1.5"><Truck strokeWidth={1.5} className="w-4 h-4" /> Shipping</Link>
              <span className="text-[#1A1C1A] border-b-2 border-[#1A1C1A] pb-1 inline-flex items-center gap-1.5"><Send strokeWidth={1.5} className="w-4 h-4" /> Broadcast</span>
            </div>
          </div>
          <Button onClick={logout} variant="outline" className="rounded-full"><LogOut className="w-4 h-4 mr-1" strokeWidth={1.5} /> Log Out</Button>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {[
            { key: "founding", label: "Founding Members", count: audienceCounts.founding },
            { key: "newsletter", label: "Newsletter", count: audienceCounts.newsletter },
            { key: "customers", label: "Google Customers", count: audienceCounts.customers },
          ].map((c) => (
            <div key={c.key} className="border border-[#E5E2DC] rounded-xl p-4 bg-[#F9F9F6]">
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold">{c.label}</p>
              <p className="mt-1 font-serif text-3xl font-light">{c.count}</p>
            </div>
          ))}
        </div>

        <div className="bg-white border border-[#E5E2DC] rounded-xl p-8 space-y-5">
          <div>
            <Label>Audience</Label>
            <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
              <SelectTrigger data-testid="broadcast-audience-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone — deduped ({audienceCounts.all})</SelectItem>
                <SelectItem value="founding">Founding members only ({audienceCounts.founding})</SelectItem>
                <SelectItem value="newsletter">Newsletter subscribers only ({audienceCounts.newsletter})</SelectItem>
                <SelectItem value="customers">Google customers only ({audienceCounts.customers})</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-stone-500">
              "Google customers" = anyone who signed in with Google. "Everyone" auto-dedupes across all three lists.
            </p>
          </div>
          <div>
            <Label>Subject line</Label>
            <Input
              data-testid="broadcast-subject-input"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="Jenova is live — your Founding Member code is inside"
            />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea
              data-testid="broadcast-body-input"
              rows={12}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder={"Write like you're texting a friend.\n\nParagraphs separate with a blank line.\n\nLine breaks preserved."}
            />
            <p className="mt-1 text-xs text-stone-500">
              Automatically wrapped in your Jenova brand template — cream card, black header, Isaiah 43:19 footer.
            </p>
          </div>

          {lastResult && (
            <div className="p-3 rounded-lg bg-[#EAE7DC] border border-[#3F5F3F]/30 text-[#3F5F3F] text-sm">
              ✓ Sent to {lastResult.sent} of {lastResult.audience_size} recipients on your last broadcast.
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              data-testid="broadcast-send-button"
              onClick={send}
              disabled={sending}
              className="bg-[#1A1C1A] hover:bg-[#3C3E3C] rounded-full"
            >
              <Mail className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> {sending ? "Sending…" : `Send to ${audienceCounts[form.audience] ?? 0} recipient${audienceCounts[form.audience] === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
